import React, { useState, useEffect, useRef } from "react";
import { ArrowDownRight, MapPin, Sparkles, Copy } from "lucide-react";
import { toast } from "sonner";

export default function Intro({ profile }) {
    if (!profile) return null;
    return (
        <section
            id="intro"
            data-testid="intro-section"
            className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 bg-ink-900 overflow-hidden"
        >
            <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" aria-hidden />
            <div
                className="absolute -top-40 right-0 w-[420px] h-[420px] rounded-full bg-brand/15 blur-[140px] opacity-70 pointer-events-none"
                aria-hidden
            />

            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                    <div className="lg:col-span-8">
                        <div className="mono-label mb-6">01 · Profile & Philosophy</div>
                        <h2
                            data-testid="intro-headline"
                            className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[7rem] text-white leading-[0.9] tracking-tighter"
                        >
                            {profile.name?.split(" ")[0] || "Akhmad"}
                            <br />
                            <span className="text-brand">
                                {profile.name?.split(" ").slice(1).join(" ") || "Ferdiansyah"}
                            </span>
                        </h2>
                        <p className="mt-8 text-lg sm:text-xl text-ink-100 max-w-2xl leading-relaxed">
                            {profile.title}. {profile.tagline}
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-3">
                            <a
                                href="#work"
                                data-testid="intro-cta-work"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector("#work")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="group inline-flex items-center gap-3 px-7 py-3.5 rounded-full bg-brand text-white font-medium hover:bg-brand-hover transition-colors shadow-[0_10px_40px_rgba(255,69,0,0.3)]"
                            >
                                View Selected Work
                                <ArrowDownRight size={18} className="group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform" />
                            </a>
                            <a
                                href="#contact"
                                data-testid="intro-cta-contact"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="inline-flex items-center gap-3 px-7 py-3.5 rounded-full border border-white/15 text-white font-medium hover:bg-white/5 transition-colors"
                            >
                                <Sparkles size={16} />
                                Discuss a Project
                            </a>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-4 text-ink-200 text-sm">
                            <div className="flex items-center gap-2">
                                <MapPin size={14} className="text-brand" />
                                {profile.location}
                            </div>
                            <span className="text-ink-300">·</span>
                            <div className="inline-flex items-center gap-2">
                                <a href={`mailto:${profile.email}`} className="hover:text-brand transition-colors">
                                    {profile.email}
                                </a>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(profile.email);
                                        toast.success("Email copied to clipboard!");
                                    }}
                                    title="Copy email to clipboard"
                                    className="p-1 rounded text-ink-300 hover:text-brand hover:bg-white/5 transition-all"
                                    aria-label="Copy email"
                                >
                                    <Copy size={13} />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-4">
                        <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
                            <AnimatedStat label="Years exp." value={`${profile.years_experience}+`} />
                            <AnimatedStat label="Projects" value={`${profile.projects_completed}+`} />
                            <AnimatedStat label="Clients" value={`${profile.happy_clients}+`} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function AnimatedStat({ label, value }) {
    const rawNum = parseInt(String(value).replace(/\D/g, ""), 10) || 0;
    const suffix = String(value).includes("+") ? "+" : "";
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !started) {
                    setStarted(true);
                }
            },
            { threshold: 0.2 }
        );
        const el = ref.current;
        if (el) observer.observe(el);
        return () => {
            if (el) observer.unobserve(el);
            observer.disconnect();
        };
    }, [started]);

    useEffect(() => {
        if (!started || rawNum <= 0) return;
        const duration = 1200;
        const steps = 28;
        const stepTime = duration / steps;
        let current = 0;
        const timer = setInterval(() => {
            current += 1;
            const progress = current / steps;
            const easeOut = 1 - (1 - progress) * (1 - progress);
            const val = Math.round(easeOut * rawNum);
            setCount(val);
            if (current >= steps) {
                setCount(rawNum);
                clearInterval(timer);
            }
        }, stepTime);
        return () => clearInterval(timer);
    }, [started, rawNum]);

    return (
        <div ref={ref} className="px-5 py-5 rounded-xl border border-white/5 bg-ink-800/40 backdrop-blur-md group hover:border-brand/30 transition-all hover:-translate-y-0.5">
            <div className="font-display text-3xl sm:text-4xl text-white tracking-tight flex items-baseline">
                <span>{started ? count : 0}</span>
                <span className="text-brand font-light">{suffix}</span>
            </div>
            <div className="mono-label mt-1 text-ink-200">{label}</div>
        </div>
    );
}
